import Experience from "@folio-2026/experience"
import Section from "@folio-2026/section"
import {
  EVENTS,
  openContactEmail,
  openContactTelegram,
  Rain,
  Sky,
} from "@folio-2026/shared"
import { gsap } from "gsap/all"
import { GLTF } from "three/examples/jsm/Addons.js"
import BloomNode, { bloom } from "three/examples/jsm/tsl/display/BloomNode.js"
import { dof } from "three/examples/jsm/tsl/display/DepthOfFieldNode.js"
import { color, emissive, mrt, output, pass, uniform } from "three/tsl"
import * as THREE from "three/webgpu"

import ContactSectionCamera from "./Camera"
import DeLorean from "./DeLorean"
import Man from "./Man"
import sources from "./sources"

export default class ContactSection extends Section {
  name = "contact"

  experience: Experience
  renderer: Experience["renderer"]
  man!: Man
  sky!: Sky
  postProcessing!: THREE.PostProcessing
  rain!: Rain
  deLorean!: DeLorean
  directionalLight!: THREE.DirectionalLight
  ambientLight!: THREE.AmbientLight
  fog!: THREE.Fog

  declare camera: ContactSectionCamera

  private contactTextBoundsCache = new WeakMap<
    HTMLElement,
    { top: number; left: number; height: number }
  >()
  private contactSmallButtonBoundsCache = new WeakMap<
    HTMLElement,
    { top: number; left: number; width: number; height: number }
  >()
  private hasRevealedContactTexts = false

  private static readonly REVEAL_STAGGER_DURATION = 0.15

  settings = {
    dofFocusDistance: uniform(2.5),
    dofFocalLength: uniform(2),
    dofBokehScale: uniform(9),
  }

  constructor() {
    super({
      name: "contact",
      sources: sources,
      folderName: "👋 Contact scene",
      expanded: false,
    })

    this.experience = Experience.getInstance()
    this.renderer = this.experience.renderer

    this.setDebug()
  }

  init() {
    super.init()

    this.textScene.texts
      .filter((text) => text.name === "app-frame")
      .forEach((text) => {
        text.material.colorNode = color("#343B4F")
      })

    this.camera = new ContactSectionCamera({ scene: this.scene })
    this.setupCamera()

    this.setup()

    this.setPostProcessing()

    this.setTextDebug()

    this.setEventListeners()
  }

  setEventListeners() {
    this.experience.on(EVENTS.Loader.CurtainRemoved, () => {
      this.cacheContactTextBounds()
      this.cacheContactSmallButtonBounds()
      this.positionTexts()
      this.positionSmallButtons()
    })

    this.experience.on(
      EVENTS.Contact.BeforeEnter,
      this.beforeEnterAnimation.bind(this),
    )

    this.experience.on(
      EVENTS.Contact.Entering,
      this.enteringAnimation.bind(this),
    )

    this.experience.on(
      EVENTS.Contact.EmailCTA.Click,
      this.emailCTAClickHandler.bind(this),
    )

    this.experience.on(
      EVENTS.Contact.TelegramCTA.Click,
      this.telegramCTAClickHandler.bind(this),
    )

    this.experience.on(
      EVENTS.Contact.XCTA.Click,
      this.xCTAClickHandler.bind(this),
    )

    this.experience.on(
      EVENTS.Contact.LinkedInCTA.Click,
      this.linkedInCTAClickHandler.bind(this),
    )

    this.experience.on(
      EVENTS.Contact.RestartCTA.Click,
      this.restartCTAClickHandler.bind(this),
    )

    this.experience.on(EVENTS.Contact.Entered, () => {
      if (this.hasRevealedContactTexts) {
        return
      }
      this.revealTexts()
    })
    this.experience.on(EVENTS.Contact.Left, () => {
      this.resetTexts()
    })
    this.experience.on(EVENTS.Audio.SetVolume, (e: unknown) => {
      const { volume } = e as { volume: number }
      if (this.experience.scrollInfo.currentSection?.name === this.name) {
        this.setVolume(volume)
      }
    })
  }

  private emailCTAClickHandler(e: unknown) {
    const { htmlElement } = e as { htmlElement?: HTMLElement }

    if (!htmlElement) return
    if (htmlElement.id !== "contact-email-cta") return

    openContactEmail()
  }

  private telegramCTAClickHandler(e: unknown) {
    const { htmlElement } = e as { htmlElement?: HTMLElement }

    if (!htmlElement) return
    if (htmlElement.id !== "contact-telegram-cta") return

    openContactTelegram()
  }

  private xCTAClickHandler(e: unknown) {
    const { htmlElement } = e as { htmlElement?: HTMLElement }

    if (!htmlElement) return
    if (htmlElement.id !== "contact-x-cta") return
    if (typeof window === "undefined") return

    window.open("https://x.com/this_is_giulio", "_blank", "noopener,noreferrer")
  }

  private linkedInCTAClickHandler(e: unknown) {
    const { htmlElement } = e as { htmlElement?: HTMLElement }

    if (!htmlElement) return
    if (htmlElement.id !== "contact-linkedin-cta") return
    if (typeof window === "undefined") return

    window.open(
      "https://www.linkedin.com/in/giuliocollesei/",
      "_blank",
      "noopener,noreferrer",
    )
  }

  private restartCTAClickHandler() {
    this.experience.lenis?.scrollTo(0, {
      duration: 1,
    })
  }

  beforeEnterAnimation() {
    if (!this.audio) {
      this.createAudio("intro-soundtrack")
    }
    this.playAudio()
  }

  enteringAnimation(e: unknown) {
    const { progress } = e as { progress: number }
    const volumeLevel = progress * this.experience.volume
    this.setVolume(volumeLevel)
  }

  setupCamera() {
    this.camera.camera.name = "contact-camera"
    this.camera.camera.position.set(0.5, 3, 2.5)
    this.camera.camera.lookAt(0.5, 3, 0)
  }

  setup() {
    /**
     * De Lorean
     */
    this.deLorean = new DeLorean({
      scene: this.scene,
      debugFolder: this.debugFolder,
      resources: this.experience.globalResources as {
        [key: string]: GLTF
      },
    })
    /**
     * Sky
     */
    this.sky = new Sky({
      scene: this.scene,
      debugFolder: this.debugFolder,
      colorTop: "#262f33",
      colorBottom: "#30353c",
    })

    /**
     * Rain
     */
    this.rain = new Rain({
      container: this.scene,
      camera: this.camera.camera,
      renderOrder: 100_010,
      renderer: this.renderer.instance,
      debugFolder: this.debugFolder,
    })

    /**
     * MAN
     */
    this.man = new Man({
      scene: this.scene,
      debugFolder: this.debugFolder,
      resources: {
        ...this.experience.globalResources,
        ...this.resources.items,
      },
    })

    // Find the object with userData.name "Plane" and add it to the scene
    const plane =
      this.experience.globalResources.blender.scene?.getObjectByName("road")
    if (plane) {
      this.scene.add(plane)
    }

    const buildings =
      this.experience.globalResources.blender.scene?.getObjectByName(
        "Contact-Buildings",
      )
    if (buildings) {
      this.scene.add(buildings)
    }

    // add fog
    this.fog = new THREE.Fog("#394346", 0, 20)
    this.scene.fog = this.fog
    /**
     * LIGHT
     */
    this.directionalLight = new THREE.DirectionalLight(0xffffff, 1)
    this.directionalLight.position.set(0, 3, 2)
    this.scene.add(this.directionalLight)

    this.ambientLight = new THREE.AmbientLight(0xacaaaa, 0.2)
    this.scene.add(this.ambientLight)

    this.setLightDebug()
    this.setFogDebug()
  }

  revealTexts() {
    if (this.hasRevealedContactTexts) {
      return
    }

    this.hasRevealedContactTexts = true

    const proxy = { progress: 0 }
    gsap.to(proxy, {
      progress: 1,
      duration: 1,
      onComplete: () => {
        const contactTexts = this.textScene.texts.filter(
          (text) => text.name === "contact",
        )
        const contactSmallButtons = this.textScene.smallButtons.filter(
          (button) => button.name === "contact",
        )
        this.positionSmallButtons()

        const revealables: { reveal: () => void; top: number }[] = []
        for (const text of contactTexts) {
          const bounds = this.contactTextBoundsCache.get(text.element)
          if (bounds) {
            revealables.push({ reveal: () => text.reveal(), top: bounds.top })
          }
        }
        for (const button of contactSmallButtons) {
          const bounds = this.contactSmallButtonBoundsCache.get(button.element)
          if (bounds) {
            revealables.push({ reveal: () => button.reveal(), top: bounds.top })
          }
        }
        revealables.sort((a, b) => a.top - b.top)
        revealables.forEach((item, index) => {
          gsap.delayedCall(
            index * ContactSection.REVEAL_STAGGER_DURATION,
            item.reveal,
          )
        })
      },
    })
  }

  private cacheContactTextBounds() {
    const contactTexts = this.textScene.texts.filter(
      (text) => text.name === "contact",
    )

    contactTexts.forEach((text) => {
      const top = text.element.offsetTop
      const left = text.element.offsetLeft
      const height = text.element.offsetHeight

      this.contactTextBoundsCache.set(text.element, {
        top,
        left,
        height,
      })
    })
  }

  private cacheContactSmallButtonBounds() {
    const contactSmallButtons = this.textScene.smallButtons.filter(
      (button) => button.name === "contact",
    )

    contactSmallButtons.forEach((button) => {
      const top = button.element.offsetTop
      const left = button.element.offsetLeft
      const width = button.element.offsetWidth
      const height = button.element.offsetHeight

      this.contactSmallButtonBoundsCache.set(button.element, {
        top,
        left,
        width,
        height,
      })
    })
  }

  positionTexts() {
    const contactTexts = this.textScene.texts.filter(
      (text) => text.name === "contact",
    )
    contactTexts.forEach((text) => {
      const bounds = this.contactTextBoundsCache.get(text.element)
      if (!bounds) {
        return
      }

      text.y = bounds.top

      const positionY = -bounds.top + this.sizes.height / 2 - bounds.height

      const positionX = bounds.left - this.sizes.width / 2

      text.mesh.position.y = positionY
      text.mesh.position.x = positionX
    })
  }

  resize() {
    if (this.camera) {
      const camera = this.camera as ContactSectionCamera
      camera.resize()
    }

    this.fbo.setSize(this.sizes.width, this.sizes.height)

    requestIdleCallback(() => {
      if (this.textScene) {
        this.textScene.resize()
        this.appFrame.resize()
      }

      this.cacheContactTextBounds()
      this.cacheContactSmallButtonBounds()

      if (this.experience.scrollInfo.currentSection?.name === this.name) {
        this.positionTexts()
        this.positionSmallButtons()
      }
    })
  }

  private positionSmallButtons() {
    const contactSmallButtons = this.textScene.smallButtons.filter(
      (button) => button.name === "contact",
    )

    contactSmallButtons.forEach((button) => {
      const bounds = this.contactSmallButtonBoundsCache.get(button.element)
      if (!bounds) {
        return
      }

      const positionX = bounds.left + bounds.width / 2 - this.sizes.width / 2
      const positionY = -bounds.top + this.sizes.height / 2 - bounds.height / 2

      button.proxyMesh.position.x = positionX
      button.proxyMesh.position.y = positionY

      button.group.position.x = positionX
      button.group.position.y = positionY
    })
  }

  resetTexts() {
    this.hasRevealedContactTexts = false

    const contactTexts = this.textScene.texts.filter(
      (text) => text.name === "contact",
    )
    contactTexts.forEach((text) => text.leave())

    const contactSmallButtons = this.textScene.smallButtons.filter(
      (button) => button.name === "contact",
    )
    contactSmallButtons.forEach((button) => button.leave())
  }

  setLightDebug() {
    if (!this.debugFolder) {
      return
    }

    const lightsFolder = this.debugFolder.addFolder({
      title: "💡 Lights",
      expanded: false,
    })

    // Directional Light
    const directionalLightFolder = lightsFolder.addFolder({
      title: "Directional Light",
      expanded: false,
    })

    const directionalLightSettings = {
      color: "#ffffff",
      intensity: this.directionalLight.intensity,
      position: {
        x: this.directionalLight.position.x,
        y: this.directionalLight.position.y,
        z: this.directionalLight.position.z,
      },
    }

    directionalLightFolder
      .addBinding(directionalLightSettings, "color", {
        label: "Color",
        view: "color",
      })
      .on("change", (ev) => {
        this.directionalLight.color.set(ev.value)
      })

    directionalLightFolder
      .addBinding(directionalLightSettings, "intensity", {
        label: "Intensity",
        min: 0,
        max: 10,
        step: 0.1,
      })
      .on("change", (ev) => {
        this.directionalLight.intensity = ev.value
      })

    directionalLightFolder
      .addBinding(directionalLightSettings, "position", {
        label: "Position",
        x: { min: -10, max: 10, step: 0.1 },
        y: { min: -10, max: 10, step: 0.1 },
        z: { min: -10, max: 10, step: 0.1 },
      })
      .on("change", (ev) => {
        this.directionalLight.position.set(ev.value.x, ev.value.y, ev.value.z)
        this.directionalLight.position.normalize()
      })

    // Ambient Light
    const ambientLightFolder = lightsFolder.addFolder({
      title: "Ambient Light",
      expanded: false,
    })

    const ambientLightSettings = {
      color: "#ffffff",
      intensity: this.ambientLight.intensity,
    }

    ambientLightFolder
      .addBinding(ambientLightSettings, "color", {
        label: "Color",
        view: "color",
      })
      .on("change", (ev) => {
        this.ambientLight.color.set(ev.value)
      })

    ambientLightFolder
      .addBinding(ambientLightSettings, "intensity", {
        label: "Intensity",
        min: 0,
        max: 5,
        step: 0.01,
      })
      .on("change", (ev) => {
        this.ambientLight.intensity = ev.value
      })
  }

  setFogDebug() {
    if (!this.debugFolder || !this.fog) {
      return
    }

    const fogFolder = this.debugFolder.addFolder({
      title: "🌫️ Fog",
      expanded: false,
    })

    // Convert fog color to hex string for the color picker
    const fogColorHex = "#" + this.fog.color.getHexString()

    const fogSettings = {
      color: fogColorHex,
      near: this.fog.near,
      far: this.fog.far,
    }

    fogFolder
      .addBinding(fogSettings, "color", {
        label: "Color",
        view: "color",
      })
      .on("change", (ev) => {
        this.fog.color.set(ev.value)
      })

    fogFolder
      .addBinding(fogSettings, "near", {
        label: "Near",
        min: 0,
        max: 200,
        step: 0.1,
      })
      .on("change", (ev) => {
        this.fog.near = ev.value
      })

    fogFolder
      .addBinding(fogSettings, "far", {
        label: "Far",
        min: 0,
        max: 200,
        step: 0.1,
      })
      .on("change", (ev) => {
        this.fog.far = ev.value
      })
  }

  setTextDebug() {
    if (!this.debugFolder) {
      return
    }

    const textsFolder = this.debugFolder.addFolder({
      title: "📝 Texts",
      expanded: false,
    })

    const textSettings = {
      color: "#ffffff",
    }

    textsFolder
      .addBinding(textSettings, "color", {
        label: "Contact Text Color",
        view: "color",
      })
      .on("change", (ev) => {
        const contactTexts = this.textScene.texts.filter(
          (text) => text.name === "contact",
        )

        if (contactTexts.length === 0) {
          console.warn("No contact texts found to update color")
          return
        }

        console.log(`Updating color for ${contactTexts.length} contact texts`)

        contactTexts.forEach((text) => {
          const newColor = color(ev.value)

          // Update both color and colorNode
          text.material.color = newColor
          text.material.colorNode = newColor

          // Force material update
          text.material.needsUpdate = true

          console.log("Updated text material:", {
            color: text.material.color,
            colorNode: text.material.colorNode,
            newColorValue: ev.value,
          })
        })
      })
  }

  setPostProcessing(): void {
    super.setPostProcessing()

    this.postProcessing = new THREE.PostProcessing(this.renderer.instance)

    const scenePass = pass(this.scene, this.camera.camera)
    scenePass.setMRT(
      mrt({
        output,
        emissive,
      }),
    )

    const outputPass = scenePass.getTextureNode()
    const emissivePass = scenePass.getTextureNode("emissive")

    const bloomPass = bloom(emissivePass, 2.5, 1.3, 0.022)

    // const scenePassColor = scenePass.getTextureNode()
    const scenePassViewZ = scenePass.getViewZNode()

    const dofPass = dof(
      outputPass,
      scenePassViewZ,
      this.settings.dofFocusDistance, // focus distance
      this.settings.dofFocalLength, // focal length
      this.settings.dofBokehScale, // bokeh scale
    )

    const color = dofPass
      .add(bloomPass)
      .add(this.textScene.postProcessing.outputNode)

    this.postProcessing.outputNode = color

    this.setPPDebug({ bloomPass })
  }

  update(params: { delta: number; elapsed: number }) {
    super.update(params)

    if (this.rain) {
      this.rain.update()
    }

    if (this.man) {
      this.man.update(params)
    }

    this.camera.update()

    this.postProcessing.render()
  }

  setPPDebug({ bloomPass }: { bloomPass: BloomNode }) {
    if (!this.debugFolder) {
      return
    }

    const pPFolder = this.debugFolder!.addFolder({
      title: "💅 Post Processing",
      expanded: false,
    })

    pPFolder.addBinding(bloomPass.strength, "value", {
      label: "Bloom strength",
      min: 0,
      max: 2.5,
      step: 0.001,
    })

    pPFolder.addBinding(bloomPass.radius, "value", {
      label: "Bloom radius",
      min: 0,
      max: 2.5,
      step: 0.001,
    })

    pPFolder.addBinding(bloomPass.threshold, "value", {
      label: "Bloom threshold",
      min: 0,
      max: 1,
      step: 0.001,
    })

    const dof = {
      focus: this.settings.dofFocusDistance.value,
      aperture: this.settings.dofFocalLength.value,
      maxBlur: this.settings.dofBokehScale.value,
    }

    pPFolder
      .addBinding(dof, "focus", {
        label: "DOF focus",
        min: 0,
        max: 5,
        step: 0.01,
      })
      .on("change", (ev) => {
        this.settings.dofFocusDistance.value = ev.value
      })

    pPFolder
      .addBinding(dof, "aperture", {
        label: "DOF aperture",
        min: 0,
        max: 5,
        step: 0.01,
      })
      .on("change", (ev) => {
        this.settings.dofFocalLength.value = ev.value
      })

    pPFolder
      .addBinding(dof, "maxBlur", {
        label: "DOF max blur",
        min: 0,
        max: 10,
        step: 0.01,
      })
      .on("change", (ev) => {
        this.settings.dofBokehScale.value = ev.value
      })
  }
}
